import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { HexColorPicker } from "react-colorful";
import QRCodeStyling from "qr-code-styling";
import { useTranslation } from 'react-i18next';


Modal.setAppElement("#root");

const QRCodeOptionsModal = ({
  isOpen,
  onRequestClose,
  onConfirm,
  defaultOptions = {},
  userLogoUrl,
  qrValue, // Pass the value to encode as prop
  logoAvailable
}) => {
  const [width, setWidth] = useState(defaultOptions.width || 1200);
  const [height, setHeight] = useState(defaultOptions.height || 1200);
  const [dotColor, setDotColor] = useState(defaultOptions.dotColor || "#000000");
  const [bgColor, setBgColor] = useState(defaultOptions.bgColor || "transparent");
  const [cornerColor, setCornerColor] = useState(defaultOptions.cornerColor || "#000000");
  const [useLogo, setUseLogo] = useState(defaultOptions.useLogo ?? true);
  const [logoSize, setLogoSize] = useState(defaultOptions.logoSize || 0.4);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const { t } = useTranslation(); // Initialize useTranslation


  const qrRef = useRef(null);
  const qrInstance = useRef(null);
  

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setWidth(defaultOptions.width || 1200);
      setHeight(defaultOptions.height || 1200);
      setDotColor(defaultOptions.dotColor || "#000000");
      setBgColor(defaultOptions.bgColor || "transparent");
      setCornerColor(defaultOptions.cornerColor || "#000000");
      setUseLogo(logoAvailable);
      setLogoSize(defaultOptions.logoSize || 0.4);
      setShowPreview(false);
      // Clean up QR code preview
      if (qrRef.current) qrRef.current.innerHTML = "";
    }
  }, [isOpen, defaultOptions]);

  // Generate QR code preview when showPreview is true
  useEffect(() => {
    if (showPreview && qrRef.current) {
      // Clean up previous QR code
      qrRef.current.innerHTML = "";
      // Create new QR code instance
      qrInstance.current = new QRCodeStyling({
        width: Number(width),
        height: Number(height),
        data: qrValue,
        image: useLogo ? (uploadedLogoUrl || userLogoUrl) : undefined,
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 1,
          imageSize: Number(logoSize),
        },
        dotsOptions: {
          color: dotColor,
          type: "rounded",
        },
        backgroundOptions: {
          color: bgColor,
        },
        cornersSquareOptions: {
          color: cornerColor,
          type: "extra-rounded",
        },
      });
      qrInstance.current.append(qrRef.current);
    }
  }, [showPreview, width, height, dotColor, bgColor, cornerColor, useLogo, logoSize, userLogoUrl, qrValue]);

  const handleGenerate = () => {
    setShowPreview(true);
  };

  const handleDownload = (ext) => {
    if (qrInstance.current) {
      qrInstance.current.download({
        name: "qr_code",
        extension: ext,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="QR Code Options"
      className="qr-modal"
      overlayClassName="qr-modal-overlay"
    >
      <h2>{t("qrCodeModal.headers.title")}</h2>
      <div className="qr-modal-row">
        <label>{t("qrCodeModal.labels.width")}:</label>
        <input
          type="number"
          min={200}
          max={2000}
          value={width}
          onChange={e => setWidth(e.target.value)}
        />
      </div>
      <div className="qr-modal-row">
        <label>{t("qrCodeModal.labels.height")}:</label>
        <input
          type="number"
          min={200}
          max={2000}
          value={height}
          onChange={e => setHeight(e.target.value)}
        />
      </div>
      <div className="qr-modal-row qr-modal-color-row">
        <div className="qr-modal-color-group">
          <label>{t("qrCodeModal.labels.pointColor")}:</label>
          <HexColorPicker color={dotColor} onChange={setDotColor} />
          <input style={{width: "120px"}} type="text" value={dotColor} onChange={e => setDotColor(e.target.value)} />
        </div>
        <div className="qr-modal-color-group">
          <label>{t("qrCodeModal.labels.backgroundColor")}:</label>
          <HexColorPicker color={bgColor === "transparent" ? "#ffffff" : bgColor} onChange={setBgColor} />
          <input
            type="text"
            value={bgColor}
            onChange={e => setBgColor(e.target.value)}
            placeholder="transparent"
            style={{width: "120px"}}
          />
        </div>
        <div className="qr-modal-color-group">
          <label>{t("qrCodeModal.labels.cornerColor")}:</label>
          <HexColorPicker color={cornerColor} onChange={setCornerColor} />
          <input style={{width: "120px"}} type="text" value={cornerColor} onChange={e => setCornerColor(e.target.value)} />
        </div>
      </div>
      <div className="qr-modal-row">
        <label>
          <input
            type="checkbox"
            checked={useLogo}
            onChange={e => setUseLogo(e.target.checked)}
          />
          {t("qrCodeModal.labels.logo")}
        </label>

        <label>
          <input
            type="checkbox"
            checked={bgColor === "transparent"}
            onChange={e => setBgColor(e.target.checked ? "transparent" : "#ffffff")}
          />
          {t("qrCodeModal.labels.transparentBg")}
        </label>
        {useLogo && (
          <div>
            <img src={uploadedLogoUrl || userLogoUrl} alt="Logo Vorschau" style={{ maxWidth: 80, maxHeight: 80, margin: "0.5em" }} />
            <label>{t("qrCodeModal.labels.logoSize")} (0.1–0.5):</label>
            <input
              type="number"
              min={0.1}
              max={0.5}
              step={0.01}
              value={logoSize}
              onChange={e => setLogoSize(e.target.value)}
            />
              <input
                id="qr-logo-upload"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = ev => setUploadedLogoUrl(ev.target.result);
                    reader.readAsDataURL(file);
                    setSelectedFileName(file.name);
                  } else {
                    setUploadedLogoUrl(null);
                    setSelectedFileName("");
                  }
                }}
              />
              <label
                htmlFor="qr-logo-upload"
                className="btn-qr-image-upload"
                style={{
                  textAlign: "center"
                }}
              >
                {selectedFileName ? selectedFileName : t("qrCodeModal.labels.uploadImage")}
              </label>
          </div>
        )}
      </div>
      <div className="qr-modal-actions">
        <button onClick={onRequestClose}>{t("qrCodeModal.labels.cancelButton")}</button>
        <button onClick={handleGenerate}>{t("qrCodeModal.labels.generateButton")}</button>
      </div>
      {showPreview && (
        <div style={{
          marginTop: "1.5em",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <div
            style={{
              width: "200px",
              height: "200px",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "1em",
            }}
          >
            <div 
              ref={qrRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                transform: `scale(${200 / width})`,
                transformOrigin: "top left",
                // width: `${width}px`,
                // height: `${height}px`,
                pointerEvents: "none",
            }}
            />
          </div>
          <div
            style={{
              fontSize: "0.85em",
              fontStyle: "italic",
              color: "#555",
              marginBottom: "1em",
              textAlign: "center",
              maxWidth: "260px"
            }}
          >
            <b>{t("qrCodeModal.message.info.QRCode1")}</b><br />
            {t("qrCodeModal.message.info.QRCode2")}
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: "1em" }}>
            <button className="btn-qr-download" onClick={() => handleDownload("png")}>Download PNG</button>
            <button className="btn-qr-download" onClick={() => handleDownload("svg")}>Download SVG</button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default QRCodeOptionsModal;